// PagarPedido v3 (2026-06-13)
// - Opción MercadoPago Checkout Pro (tarjeta) — todos los clientes
// - Opción Transferencia bancaria — solo si profile.transferencia_habilitada
//   muestra datos bancarios desde app_config + subida opcional de comprobante
//   marca el pedido con metodo='transferencia' y queda pendiente_pago hasta que admin confirme

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { initMpDeviceId, getMpDeviceId } from '../../lib/mpDeviceId'

// app_config mezcla valores planos ("UYU") con JSON-quoted ("\"texto\""). Devuelve siempre el
// texto limpio, y '' si queda vacío (para no pintar filas fantasma tipo Sucursal: "").
function cleanConfig(v) {
  if (v === null || v === undefined) return ''
  let s = typeof v === 'string' ? v : String(v)
  try { const p = JSON.parse(s); if (typeof p === 'string') s = p } catch { /* valor plano */ }
  return s.trim()
}

export default function PagarPedido() {
  const { profile, user } = useAuth()
  const navigate = useNavigate()
  const { pedidoId } = useParams()

  const [pedido, setPedido] = useState(null)
  const [bankConfig, setBankConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [redirigiendo, setRedirigiendo] = useState(false)
  const [vista, setVista] = useState('opciones')   // opciones | transferencia
  const [comprobante, setComprobante] = useState(null) // File
  const [subiendo, setSubiendo] = useState(false)
  const [marcandoTransferido, setMarcandoTransferido] = useState(false)

  const transferenciaHabilitada = !!profile?.transferencia_habilitada

  useEffect(() => {
    try { console.log('%c[CHK:pagar-pedido]', 'color:#10B981;font-weight:700', 'PagarPedido MONTADO', { pedidoId, userId: user?.id || null }) } catch { /* noop */ }
    initMpDeviceId() // precarga la huella antifraude de MP para tenerla lista al pagar
    let cancel = false
    async function load() {
      if (!pedidoId) { setError('Falta pedido'); setLoading(false); return }
      try {
        const { data, error: pe } = await supabase
          .from('pedidos')
          .select('id, numero_orden, monto_total, estado, kit_codigo, items, fecha_entrega_programada, metodo_pago, comprobante_transferencia_url')
          .eq('id', pedidoId)
          .single()
        if (pe) throw pe
        if (!cancel) setPedido(data)
        // Si ya tiene metodo transferencia, abrir directo esa vista
        if (!cancel && data?.metodo_pago === 'transferencia' && data?.estado === 'pendiente_pago') {
          setVista('transferencia')
        }

        // Cargar config bancaria solo si está habilitado
        if (transferenciaHabilitada) {
          const { data: configs } = await supabase
            .from('app_config')
            .select('key, value')
            .like('key', 'BANK_%')
          // app_config guarda algunos valores JSON-quoted ("texto") y otros planos. Sin limpiar,
          // al cliente le aparecían las instrucciones ENTRE COMILLAS y una fila "Sucursal" con "".
          const cfg = {}
          ;(configs || []).forEach(c => {
            const limpio = cleanConfig(c.value)
            if (limpio) cfg[c.key] = limpio   // descarta vacíos → no se pinta la fila
          })
          if (!cancel) setBankConfig(cfg)
        }
      } catch (e) {
        if (!cancel) setError(e.message)
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => { cancel = true }
  }, [pedidoId, transferenciaHabilitada])

  async function pagarConMP() {
    setError(null); setRedirigiendo(true)
    try { console.log('%c[MPBTN]', 'color:#009ee3;font-weight:700', 'tap Pagar MP', { pedidoId: pedido?.id, userId: user?.id || null, email: user?.email || profile?.email || null, nombre: profile?.nombre || null }) } catch { /* noop */ }
    try {
      const nombre = (profile?.nombre || '').trim()
      const email = user?.email || profile?.email || ''
      if (nombre.length < 2 || !email) {
        throw new Error('Te faltan datos del perfil (nombre y email). Completalos antes de pagar.')
      }
      const deviceId = await getMpDeviceId() // huella antifraude MP (best-effort, no bloquea)
      const { data, error: e } = await supabase.functions.invoke('mp-create-preference', {
        body: { pedido_id: pedido.id, email, device_id: deviceId || undefined },
      })
      try { console.log('%c[MPBTN]', 'color:#009ee3;font-weight:700', 'mp-create-preference resp', { err: e?.message || null, dataError: data?.error || null, hasInitPoint: !!data?.init_point }) } catch { /* noop */ }
      if (e) throw e
      if (data?.error) throw new Error(data.error + (data.detalle ? ': ' + JSON.stringify(data.detalle).slice(0, 200) : ''))
      if (!data?.init_point) throw new Error('MercadoPago no devolvió URL de pago')
      try { console.log('%c[MPBTN]', 'color:#009ee3;font-weight:700', 'window.location.href =', data.init_point) } catch { /* noop */ }
      window.location.href = data.init_point
    } catch (e) {
      try { console.warn('[MPBTN] CATCH', e?.message || String(e), e) } catch { /* noop */ }
      setError(e.message || String(e))
      setRedirigiendo(false)
      // Rastro del fallo de pago en la cuenta, para verlo después desde la ficha del usuario.
      try {
        supabase.rpc('registrar_error_mi_carrito', {
          p_error: `[pago MP] ${e?.message || String(e)}`,
          p_paso: 'pago_mercadopago',
        })
      } catch { /* best-effort */ }
    }
  }

  async function elegirTransferencia() {
    setError(null)
    try {
      const { error: e } = await supabase
        .from('pedidos')
        .update({ metodo_pago: 'transferencia', pago_referencia: pedido.numero_orden })
        .eq('id', pedido.id)
      if (e) throw e
      setVista('transferencia')
    } catch (e) {
      setError(e.message)
    }
  }

  async function subirComprobante() {
    if (!comprobante) return
    setSubiendo(true)
    setError(null)
    try {
      const ext = comprobante.name.split('.').pop()
      const path = `${pedido.id}/${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage
        .from('comprobantes-transferencia')
        .upload(path, comprobante, { upsert: true, contentType: comprobante.type })
      if (upErr) throw upErr
      const { error: updErr } = await supabase
        .from('pedidos')
        .update({
          comprobante_transferencia_url: path,
          comprobante_transferencia_uploaded_at: new Date().toISOString(),
        })
        .eq('id', pedido.id)
      if (updErr) throw updErr
      const { data: refresh } = await supabase.from('pedidos').select('*').eq('id', pedido.id).single()
      setPedido(refresh)
      setComprobante(null)
    } catch (e) {
      setError(e.message)
    }
    setSubiendo(false)
  }

  async function marcarTransferido() {
    if (!confirm('¿Confirmar que ya hiciste la transferencia? Vamos a revisar nuestro banco y te avisamos por email + WhatsApp cuando se acredite.')) return
    setMarcandoTransferido(true)
    setError(null)
    try {
      // Actualizamos las notas del pedido para que el admin sepa que el cliente avisó
      const nota = `Cliente marcó como transferido el ${new Date().toLocaleString('es-UY')}`
      const { error: e } = await supabase
        .from('pedidos')
        .update({ notas: nota })
        .eq('id', pedido.id)
      if (e) throw e
      navigate('/cliente?transferencia=enviada')
    } catch (e) {
      setError(e.message)
    }
    setMarcandoTransferido(false)
  }

  if (loading) {
    return <ClienteLayout title="Pagar tu pedido"><main className="pp-main"><div className="pp-card">Cargando…</div></main><PpStyles /></ClienteLayout>
  }

  if (pedido?.estado === 'pagado') {
    return (
      <ClienteLayout title="Pagar tu pedido">
        <main className="pp-main">
          <div className="pp-card" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 64, marginBottom: 12 }}>✅</div>
            <h2 style={{ color: '#1a3461', margin: '0 0 8px' }}>¡Pedido ya pago!</h2>
            <p style={{ color: '#374151', margin: '0 0 20px' }}>
              Te avisamos por WhatsApp cuando salga el repartidor.
            </p>
            <button onClick={() => navigate('/cliente')}
              style={{ padding: '13px 24px', background: '#1a3461', color: '#fff', border: 0, borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
              Volver al panel →
            </button>
          </div>
        </main>
        <PpStyles />
      </ClienteLayout>
    )
  }

  const nombreOk = (profile?.nombre || '').trim().length > 1
  const emailOk = !!(user?.email || profile?.email)
  const datosOk = nombreOk && emailOk
  const monto = Number(pedido?.monto_total || 0).toLocaleString('es-UY')
  const tieneComprobante = !!pedido?.comprobante_transferencia_url

  return (
    <ClienteLayout title="Pagar tu pedido">
      <main className="pp-main">
        <div className="pp-card">
          <div className="pp-header">
            <h2>💳 Pagar tu pedido</h2>
            <div className="pp-sub">
              Pedido <strong>{pedido?.numero_orden}</strong> · ${monto}
            </div>
          </div>

          {!datosOk && (
            <div className="pp-alert-warn">
              <div style={{ fontWeight: 700, fontSize: 13, color: '#78350f', marginBottom: 8 }}>
                ⚠️ Te faltan datos antes de pagar
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                {!nombreOk && <li>✗ Falta tu nombre</li>}
                {!emailOk && <li>✗ Falta tu email</li>}
              </ul>
              <div style={{ marginTop: 10 }}>
                <button onClick={() => navigate('/cliente/perfil')} className="pp-btn-dark-sm">
                  Completar mi perfil →
                </button>
              </div>
            </div>
          )}

          {error && <div className="pp-err">{error}</div>}

          {/* ──────────── VISTA: ELEGIR MÉTODO ──────────── */}
          {vista === 'opciones' && (
            <>
              <div className="pp-secure" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src="/mp-logo.svg" alt="MercadoPago" width="72" style={{ height: 'auto', flexShrink: 0 }} />
                <div>
                  <strong>🔒 Pago seguro · Procesado por MercadoPago</strong>
                  <div style={{ fontSize: 12, marginTop: 2 }}>Tarjetas, banca digital, Redpagos, Abitab, transferencia y cuotas sin recargo.</div>
                </div>
              </div>

              <button
                onClick={pagarConMP}
                disabled={!datosOk || redirigiendo}
                className="pp-pagar-btn"
                style={{
                  width: '100%', padding: '16px 24px',
                  background: datosOk ? '#1a3461' : '#94a3b8',
                  color: '#fff', border: 0, borderRadius: 12,
                  fontWeight: 700, fontSize: 16,
                  cursor: (!datosOk || redirigiendo) ? 'not-allowed' : 'pointer',
                }}
              >
                {redirigiendo ? 'Redirigiendo a MercadoPago…' : `💳 Pagar con tarjeta · $${monto}`}
              </button>

              {transferenciaHabilitada && (
                <>
                  <div className="pp-divider"><span>o</span></div>
                  <button
                    onClick={elegirTransferencia}
                    disabled={!datosOk || redirigiendo}
                    className="pp-pagar-btn"
                    style={{
                      width: '100%', padding: '16px 24px',
                      background: datosOk ? '#fff' : '#f1f5f9',
                      color: datosOk ? '#1a3461' : '#94a3b8',
                      border: '2px solid ' + (datosOk ? '#1a3461' : '#cbd5e1'),
                      borderRadius: 12, fontWeight: 700, fontSize: 16,
                      cursor: !datosOk ? 'not-allowed' : 'pointer',
                    }}
                  >
                    🏦 Pagar por transferencia bancaria
                  </button>
                  <p style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>
                    Tenés esta opción habilitada porque la pediste especialmente.<br />
                    Acreditamos en 24-48hs hábiles y te avisamos.
                  </p>
                </>
              )}

              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <button type="button" onClick={() => navigate('/cliente')} disabled={redirigiendo}
                  style={{ padding: '10px 18px', border: 'none', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  ← Pagar después
                </button>
              </div>
            </>
          )}

          {/* ──────────── VISTA: TRANSFERENCIA ──────────── */}
          {vista === 'transferencia' && bankConfig && (
            <>
              <div className="pp-bank-info">
                <h3 style={{ margin: '0 0 12px', color: '#1A3461', fontSize: 15 }}>🏦 Datos para la transferencia</h3>
                <BankRow label="Banco" value={bankConfig.BANK_BANCO || '—'} />
                <BankRow label="Titular" value={bankConfig.BANK_TITULAR || '—'} />
                <BankRow label="RUT" value={bankConfig.BANK_RUT || '—'} copiable />
                <BankRow label="Tipo cuenta" value={bankConfig.BANK_TIPO_CUENTA || '—'} />
                <BankRow label="Moneda" value={bankConfig.BANK_MONEDA || 'UYU'} />
                <BankRow label="N° cuenta" value={bankConfig.BANK_NUMERO || '—'} copiable highlight mono />
                {bankConfig.BANK_SUCURSAL && <BankRow label="Sucursal" value={bankConfig.BANK_SUCURSAL} />}
                {bankConfig.BANK_ALIAS && <BankRow label="Alias" value={bankConfig.BANK_ALIAS} copiable />}
                <BankRow label="Monto" value={`$${monto}`} highlight valueStyle={{ fontSize: 18, color: '#16a34a' }} />
                <BankRow label="Referencia" value={pedido?.numero_orden} copiable highlight mono valueStyle={{ color: '#dc2626' }} important />
                {bankConfig.BANK_INSTRUCCIONES && (
                  <p style={{ fontSize: 12, color: '#475569', marginTop: 12, lineHeight: 1.5, padding: 10, background: '#fff', borderRadius: 8 }}>
                    {bankConfig.BANK_INSTRUCCIONES}
                  </p>
                )}
              </div>

              <div className="pp-comprobante-section">
                <h3 style={{ margin: '0 0 10px', color: '#1A3461', fontSize: 14 }}>📄 Comprobante (opcional)</h3>
                {tieneComprobante ? (
                  <div style={{ padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, color: '#065f46', fontSize: 13 }}>
                    ✓ Comprobante subido correctamente. Lo vamos a revisar.
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>
                      Subir el comprobante acelera la confirmación. Aceptamos imagen (JPG, PNG) o PDF, máx. 5MB.
                    </p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={e => setComprobante(e.target.files?.[0] || null)}
                      style={{ width: '100%', padding: 8, border: '1px dashed #cbd5e1', borderRadius: 8, marginBottom: 10 }}
                    />
                    {comprobante && (
                      <button
                        onClick={subirComprobante}
                        disabled={subiendo}
                        className="pp-btn-dark-sm"
                        style={{ width: '100%' }}
                      >
                        {subiendo ? 'Subiendo…' : '⬆ Subir comprobante'}
                      </button>
                    )}
                  </>
                )}
              </div>

              <button
                onClick={marcarTransferido}
                disabled={marcandoTransferido}
                className="pp-pagar-btn"
                style={{
                  width: '100%', padding: '16px 24px',
                  background: '#16a34a', color: '#fff', border: 0, borderRadius: 12,
                  fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 16,
                }}
              >
                {marcandoTransferido ? 'Procesando…' : '✓ Ya hice la transferencia'}
              </button>

              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <button type="button" onClick={() => setVista('opciones')}
                  style={{ padding: '10px 18px', border: 'none', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  ← Volver a opciones de pago
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      <PpStyles />
    </ClienteLayout>
  )
}

// Componente con botón copiar al portapapeles + soporte mobile-friendly
function BankRow({ label, value, copiable, highlight, mono, important, valueStyle = {} }) {
  const [copied, setCopied] = useState(false)
  async function copiar() {
    try {
      await navigator.clipboard.writeText(String(value))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }
  return (
    <div className={`pp-bank-row${highlight ? ' pp-bank-row-highlight' : ''}${important ? ' pp-bank-row-important' : ''}`}>
      <span className="pp-bank-label">{label}</span>
      <div className="pp-bank-value-wrap">
        <strong style={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 16 : 14, ...valueStyle }}>{value}</strong>
        {copiable && (
          <button
            type="button"
            onClick={copiar}
            className="pp-copy-btn"
            title="Copiar al portapapeles"
          >
            {copied ? '✓' : '📋'}
          </button>
        )}
      </div>
    </div>
  )
}

function PpStyles() {
  return (
    <style>{`
      .pp-main { max-width: 600px; width: 100%; margin: 0 auto; padding: 28px 16px 60px; flex: 1; box-sizing: border-box; }
      .pp-card { background: #fff; border-radius: 20px; box-shadow: 0 8px 40px rgba(26,52,97,.08); border: 1px solid rgba(26,52,97,.05); padding: 24px; }
      @media (min-width: 640px) { .pp-card { padding: 32px } }
      .pp-header { padding-bottom: 16px; border-bottom: 1px solid #eef0f4; margin-bottom: 18px; }
      .pp-header h2 { margin: 0 0 6px; color: #1a3461; font-size: 22px; }
      .pp-sub { color: #6b7280; font-size: 13px; line-height: 1.4; }
      .pp-secure { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 10px; color: #065f46; font-size: 13px; line-height: 1.4; margin-bottom: 18px; }
      .pp-err { padding: 10px 12px; background: #fee2e2; border: 1px solid #fca5a5; border-radius: 8px; color: #991b1b; font-size: 13px; margin-bottom: 12px; }
      .pp-alert-warn { margin: 4px 0 16px; padding: 14px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 10px; }
      .pp-pagar-btn:hover:not(:disabled) { filter: brightness(0.92); }
      .pp-divider { display: flex; align-items: center; margin: 18px 0; color: #94a3b8; font-size: 12px; }
      .pp-divider::before, .pp-divider::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
      .pp-divider span { padding: 0 12px; }
      .pp-btn-dark-sm { padding: 10px 18px; background: #1a3461; color: #fff; border: 0; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }
      .pp-btn-dark-sm:hover { background: #0f2440; }
      .pp-bank-info { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin: 18px 0; }
      .pp-bank-row { display: flex; justify-content: space-between; align-items: center; padding: 9px 0; border-bottom: 1px solid #f1f5f9; font-size: 13px; color: #1a3461; gap: 12px; }
      .pp-bank-row:last-child { border-bottom: 0; }
      .pp-bank-row-highlight { background: rgba(0,168,181,0.08); padding: 12px 10px; border-radius: 8px; margin: 4px -6px; border-bottom: 0; }
      .pp-bank-row-important { background: rgba(220,38,38,0.06); border: 1.5px dashed rgba(220,38,38,0.3); }
      .pp-bank-label { font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; flex-shrink: 0; }
      .pp-bank-value-wrap { display: flex; align-items: center; gap: 6px; flex: 1; justify-content: flex-end; text-align: right; }
      .pp-copy-btn { background: #fff; border: 1px solid #cbd5e1; color: #475569; width: 28px; height: 28px; border-radius: 6px; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
      .pp-copy-btn:hover { background: #f1f5f9; border-color: #94a3b8; }
      .pp-comprobante-section { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-top: 12px; }

      /* Mobile (≤480px): rows en vertical para no apretar */
      @media (max-width: 480px) {
        .pp-bank-row { flex-direction: column; align-items: flex-start; gap: 4px; padding: 10px 0; }
        .pp-bank-value-wrap { justify-content: flex-start; width: 100%; }
      }
    `}</style>
  )
}
